import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Standalone axios instance — this page is PUBLIC (token-based, no auth cookies needed)
const publicApi = axios.create({ baseURL: API_BASE, headers: { "Content-Type": "application/json" } });

export default function Preferences() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [prefs, setPrefs] = useState({
        email_new_suggestions: true,
        email_announcements: true,
        email_order_confirmed: true,
        email_order_en_traitement: false,
        email_order_en_production: false,
        email_order_en_livraison: true,
        email_order_livree: false,
    });
    const [savedOnce, setSavedOnce] = useState(false);

    useEffect(() => {
        if (!token) {
            setError("Lien invalide : aucun jeton fourni.");
            setLoading(false);
            return;
        }
        publicApi
            .get(`/preferences`, { params: { token } })
            .then((res) => {
                setUser({
                    email: res.data.email,
                    prenom: res.data.prenom,
                    raison_sociale: res.data.raison_sociale,
                });
                setPrefs({
                    email_new_suggestions: !!res.data.email_new_suggestions,
                    email_announcements: !!res.data.email_announcements,
                    email_order_confirmed: res.data.email_order_confirmed !== false,
                    email_order_en_traitement: !!res.data.email_order_en_traitement,
                    email_order_en_production: !!res.data.email_order_en_production,
                    email_order_en_livraison: res.data.email_order_en_livraison !== false,
                    email_order_livree: !!res.data.email_order_livree,
                });
            })
            .catch((e) => {
                setError(e?.response?.data?.detail || "Lien invalide ou expiré.");
            })
            .finally(() => setLoading(false));
    }, [token]);

    const save = async () => {
        setSaving(true);
        try {
            await publicApi.post(`/preferences`, prefs, { params: { token } });
            toast.success("Préférences enregistrées");
            setSavedOnce(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impossible d'enregistrer.");
        } finally {
            setSaving(false);
        }
    };

    const unsubscribeAll = () => {
        setPrefs({
            email_new_suggestions: false,
            email_announcements: false,
            email_order_confirmed: false,
            email_order_en_traitement: false,
            email_order_en_production: false,
            email_order_en_livraison: false,
            email_order_livree: false,
        });
    };

    if (loading) {
        return (
            <section className="max-w-2xl mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="preferences-loading">
                <p className="text-[#A0A0A0]">Chargement…</p>
            </section>
        );
    }

    if (error) {
        return (
            <section className="max-w-2xl mx-auto px-5 md:px-10 py-16 md:py-24 text-center" data-testid="preferences-error">
                <p className="bc-label-small">Préférences email</p>
                <h1 className="font-serif text-[#D4BC7C] text-3xl md:text-4xl mt-4">Lien invalide</h1>
                <div className="bc-hairline-gold my-6 mx-auto max-w-xs" />
                <p className="text-[#F2ECE0]/80 max-w-md mx-auto">{error}</p>
                <p className="text-sm text-[#A0A0A0] mt-6">
                    Ouvrez le lien depuis un de nos emails récents, ou contactez-nous à{" "}
                    <a href="mailto:info@boucherie-costa.ch" className="text-[#D4BC7C] underline">info@boucherie-costa.ch</a>.
                </p>
                <Link to="/" className="bc-btn mt-8 inline-flex">Retour à l&apos;accueil</Link>
            </section>
        );
    }

    return (
        <section className="max-w-2xl mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="preferences-page">
            <p className="bc-label-small">Espace Pro</p>
            <h1 className="font-serif text-[#D4BC7C] text-3xl md:text-4xl mt-4">Vos préférences email</h1>
            <div className="bc-hairline-gold my-6" />
            <p className="text-sm text-[#F2ECE0]/80">
                Bonjour {user?.prenom || "cher partenaire"}
                {user?.raison_sociale ? ` (${user.raison_sociale})` : ""}. Choisissez les types d&apos;emails
                que vous souhaitez recevoir de la part de la Boucherie Costa. Les emails transactionnels
                (validation de compte, statut des commandes, réinitialisation de mot de passe) restent
                envoyés dans tous les cas.
            </p>

            <div className="bc-card mt-8 space-y-6" data-testid="preferences-form">
                <p className="bc-label-small">Contenus éditoriaux</p>
                <PrefRow
                    label="Nouvelles suggestions du moment"
                    hint="Recevoir un email quand une nouvelle suggestion est publiée."
                    checked={prefs.email_new_suggestions}
                    onChange={(v) => setPrefs((p) => ({ ...p, email_new_suggestions: v }))}
                    testId="pref-suggestions"
                />
                <div className="bc-hairline" />
                <PrefRow
                    label="Informations importantes (bandeau Flash Info)"
                    hint="Fermetures exceptionnelles, changements d&apos;horaires, vacances."
                    checked={prefs.email_announcements}
                    onChange={(v) => setPrefs((p) => ({ ...p, email_announcements: v }))}
                    testId="pref-announcements"
                />

                <div className="bc-hairline" />
                <p className="bc-label-small">Statut de mes commandes</p>
                <PrefRow
                    label="Commande confirmée (30 min après validation)"
                    hint="Confirmation « Leonel prépare votre commande » — recommandé."
                    checked={prefs.email_order_confirmed}
                    onChange={(v) => setPrefs((p) => ({ ...p, email_order_confirmed: v }))}
                    testId="pref-order-confirmed"
                />
                <div className="bc-hairline" />
                <PrefRow
                    label="Statut « En traitement »"
                    hint="Notification au moment de la validation initiale."
                    checked={prefs.email_order_en_traitement}
                    onChange={(v) => setPrefs((p) => ({ ...p, email_order_en_traitement: v }))}
                    testId="pref-order-en_traitement"
                />
                <div className="bc-hairline" />
                <PrefRow
                    label="Statut « En production »"
                    hint="Notification lorsque vos articles sont en préparation."
                    checked={prefs.email_order_en_production}
                    onChange={(v) => setPrefs((p) => ({ ...p, email_order_en_production: v }))}
                    testId="pref-order-en_production"
                />
                <div className="bc-hairline" />
                <PrefRow
                    label="Statut « En cours de livraison »"
                    hint="Notification lorsque la commande est en route — recommandé."
                    checked={prefs.email_order_en_livraison}
                    onChange={(v) => setPrefs((p) => ({ ...p, email_order_en_livraison: v }))}
                    testId="pref-order-en_livraison"
                />
                <div className="bc-hairline" />
                <PrefRow
                    label="Statut « Livrée »"
                    hint="Notification finale à la réception."
                    checked={prefs.email_order_livree}
                    onChange={(v) => setPrefs((p) => ({ ...p, email_order_livree: v }))}
                    testId="pref-order-livree"
                />

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                    <button
                        className="bc-btn flex-1"
                        onClick={save}
                        disabled={saving}
                        data-testid="pref-save"
                    >
                        {saving ? "Enregistrement…" : "Enregistrer mes préférences"}
                    </button>
                    <button
                        className="bc-btn bc-btn--ghost"
                        onClick={unsubscribeAll}
                        disabled={saving}
                        data-testid="pref-unsub-all"
                    >
                        Tout désactiver
                    </button>
                </div>

                {savedOnce && (
                    <div className="flex items-center gap-2 text-[#D4BC7C] text-sm" data-testid="pref-saved">
                        <CheckCircle2 size={16} /> Vos préférences ont bien été mises à jour.
                    </div>
                )}
            </div>

            <p className="text-xs text-[#7a7a7a] mt-8 text-center">
                Compte email : <span className="text-[#A0A0A0]">{user?.email}</span>
            </p>
        </section>
    );
}

function PrefRow({ label, hint, checked, onChange, testId }) {
    return (
        <label className="flex items-start justify-between gap-4 cursor-pointer" data-testid={`${testId}-row`}>
            <div className="flex-1">
                <p className="text-[#F2ECE0] text-sm font-medium">{label}</p>
                <p className="text-xs text-[#A0A0A0] mt-1">{hint}</p>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                onClick={() => onChange(!checked)}
                data-testid={testId}
                style={{
                    width: "52px",
                    height: "28px",
                    background: checked ? "#D4BC7C" : "rgba(212,188,124,0.15)",
                    border: "1px solid #D4BC7C",
                    position: "relative",
                    cursor: "pointer",
                    transition: "background-color 0.25s ease",
                    flexShrink: 0,
                }}
            >
                <span
                    style={{
                        position: "absolute",
                        top: "2px",
                        left: checked ? "26px" : "3px",
                        width: "22px",
                        height: "22px",
                        background: checked ? "#000" : "#D4BC7C",
                        transition: "left 0.25s cubic-bezier(0.4,0,0.2,1)",
                    }}
                />
            </button>
        </label>
    );
}
